// Port of BivouacEngine/SleepTonightPlanner.swift.
//
// Pure: no DOM, no clock (every time derives from `request.now`), no I/O — safe to run in a Web Worker.

import type {
  Availability,
  BivouacCandidate,
  BivouacReason,
  Confidence,
  EpochMs,
  GeoBounds,
  GeoCoordinate,
  GeoDataQuerying,
  GeoPolygon,
  OvernightCategory,
  OvernightPreference,
  POICategory,
  PointOfInterest,
  RegulatoryState,
  SuitabilityStatus,
  ThunderstormRisk,
  WeatherForecast,
  WeatherSnapshot,
} from '../core/models';
import {
  allOvernightCategories,
  compareFreshness,
  compareThunderstormRisk,
  forecastFreshness,
  minConfidence,
  overnightCategoryOf,
  preferredCategories,
  seasonContains,
} from '../core/domain';
import { HikingTime } from '../core/elevation';
import { boundsExpanded, boundsOf, isValidCoordinate, type PolylineProjection } from '../core/geo';
import { daylight as solarDaylight, type DayLight } from '../core/solar';
import { HikingTimeEstimator } from '../nav/hikingTimeEstimator';
import type { RouteTrack } from '../nav/routeTrack';
import { BivouacLog } from './bivouacLog';
import type { BivouacScoringConfiguration } from './bivouacScoringConfiguration';
import { BivouacZoneGenerator } from './bivouacZoneGenerator';
import {
  OvernightFacilities,
  POIProximity,
  WeatherAdjustment,
  confidenceRankValue,
  containsReason,
  sliceTrack,
} from './engineSupport';
import { RegulationResolver } from './regulationResolver';
import {
  makeSleepTonightRequest,
  type OvernightGroup,
  type OvernightOption,
  type PlannerWarning,
  type SleepTonightRequest,
  type SleepTonightRequestInit,
  type SleepTonightResult,
} from './sleepTonightModels';
import { SuitabilityClassifier } from './suitabilityClassifier';

/** Walking estimate from the hiker to a target. */
interface Leg {
  projection: PolylineProjection;
  /** Signed distance along the route (negative = behind). */
  delta: number;
  detour: number;
  total: number;
  ascent: number;
  descent: number;
  /** seconds */
  duration: number;
  arrival: EpochMs;
}

/** The hiker's starting point relative to the route. */
interface Origin {
  along: number;
  now: EpochMs;
  returnDistance: number;
  returnAscent: number;
  returnDescent: number;
}

/** Search stretch and limits for one request. */
interface SearchRange {
  start: number;
  end: number;
  bounds: GeoBounds;
  projectionWindow: number;
  straightLineDetourLimit: number;
}

/** Everything the option builders need. */
interface PlanningContext {
  request: SleepTonightRequest;
  origin: Origin;
  sunset?: EpochMs;
  civilDusk?: EpochMs;
  forecast?: WeatherForecast;
  searchRange: SearchRange;
}

interface OptionInput {
  id: string;
  category: OvernightCategory;
  name?: string;
  poi?: PointOfInterest;
  candidate?: BivouacCandidate;
  coordinate: GeoCoordinate;
  area?: GeoPolygon;
  leg: Leg;
  context: PlanningContext;
  forecastAtArrival?: WeatherSnapshot;
  nearestWater?: number;
  regulatoryState: RegulatoryState;
  regulatoryConfidence: Confidence;
  sourceConfidence: Confidence;
  availability: Availability;
  status: SuitabilityStatus;
  reasons: BivouacReason[];
}

const waterCategory: ReadonlySet<POICategory> = new Set<POICategory>(['water']);
const compareStrings = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);
const offlineEverywhere = (): Availability => 'availableOffline';

/**
 * Answers "Where could I sleep tonight?" from the hiker's position, the route, the offline data and the (possibly
 * cached) forecast. Works fully offline; all times derive from `request.now`.
 *
 * Time budget = min(desiredStopTime ?? civil dusk, now + maxAdditionalWalking) − now. Options beyond budget are
 * excluded except that each category keeps its single nearest option flagged (arrivesAfterSunset /
 * arrivesAfterDesiredStop) so the hiker always sees alternatives. Official POIs get regulatoryState `official`.
 * Ranking: status, arrival before dusk, effort, confidence.
 *
 * - **Position.** The hiker is projected on the route; > 250 m away → `offRoute`, > 3 km → also
 *   `outsideRouteCorridor`. Beyond 50 m the walk back to the route (cross-track × 1.3, plus the elevation difference)
 *   is added to every option.
 * - **Daylight** comes from the solar calculator (offline); civil dusk is the default stop time. Before civil dawn the
 *   previous evening's sunset/dusk apply (it is still night: `afterSunset`, no walking budget).
 * - **Weather.** An expired forecast is never used; the plan then carries `noWeather` (plus `staleWeather`).
 * - **Options.** Overnight POIs whose estimated detour (straight-line distance to the route × 1.3) is within
 *   `maxDetourMeters`, between `behindLimitMeters` behind and the reachable distance ahead, plus bivouac search zones
 *   from `BivouacZoneGenerator` over the same stretch.
 * - **Regulation.** `official` is assigned only to designated facilities. Other shelters are resolved like terrain and
 *   are never shown green. Bivouac search areas are never `official`.
 * - **Grouping.** One group per category, ordered by the preference's categories then the remaining ones; at most
 *   `maximumOptionsPerCategory` options per group; empty groups omitted.
 */
export class SleepTonightPlanner {
  /** Cross-track distance (m) beyond which the hiker is reported off route. */
  static readonly offRouteThresholdMeters = 250;
  /** Cross-track distance (m) beyond which route-based estimates are unreliable. */
  static readonly routeCorridorMeters = 3000;
  /** Minutes before sunset below which `lateInDay` is reported. */
  static readonly lateInDayMinutes = 90;
  /** Walking detour ≈ straight-line distance × this factor. */
  static readonly detourFactor = 1.3;
  /** Maximum options shown per category. */
  static readonly maximumOptionsPerCategory = 8;
  /** Within this distance of the route the hiker is considered on it (GPS noise). */
  static readonly onRouteToleranceMeters = 50;
  /** Options this close behind still count when `includeOptionsBehind` is false (the hut next to you). */
  static readonly behindToleranceMeters = 50;
  /** Water POIs are looked up within this radius (m) of an option. */
  static readonly waterSearchRadiusMeters = 2000;
  /** Zones requested from the generator before the budget and per-category limits apply. */
  static readonly zoneFetchLimit = 100;
  /** Length (s) of the "night" window after the stop time used for weather warnings. */
  static readonly nightDuration = 12 * 3600;

  /** Route being followed (travel direction = increasing distance). */
  readonly track: RouteTrack;
  /** Offline data (POIs, protected areas, regulations, coverage, terrain). */
  readonly data: GeoDataQuerying;
  /** Walking-time model (personal factor applied). */
  readonly estimator: HikingTimeEstimator;
  /** Bivouac search-zone generator (its evaluator's configuration also drives the weather warnings). */
  readonly zoneGenerator: BivouacZoneGenerator;
  /** Regulatory resolution for non-designated shelters. */
  readonly resolver: RegulationResolver;

  constructor(
    track: RouteTrack,
    data: GeoDataQuerying,
    estimator: HikingTimeEstimator = new HikingTimeEstimator(),
    zoneGenerator: BivouacZoneGenerator = new BivouacZoneGenerator(),
    resolver: RegulationResolver = new RegulationResolver(),
  ) {
    this.track = track;
    this.data = data;
    this.estimator = estimator;
    this.zoneGenerator = zoneGenerator;
    this.resolver = resolver;
  }

  // MARK: Plan

  /** Computes the grouped overnight options for `request` (defaults applied with `makeSleepTonightRequest`). */
  plan(
    requestInit: SleepTonightRequestInit,
    forecast?: WeatherForecast,
    availability: (coordinate: GeoCoordinate) => Availability = offlineEverywhere,
  ): SleepTonightResult {
    const request = makeSleepTonightRequest(requestInit);
    const now = request.now;
    const configuration = this.zoneGenerator.evaluator.configuration;

    // Daylight and time budget.
    const light = SleepTonightPlanner.daylight(now, request.position);
    const maxWalking = Number.isFinite(request.maxAdditionalWalking) ? Math.max(0, request.maxAdditionalWalking) : 0;
    const latestByWalking = now + maxWalking * 1000;
    const stopTime = Math.min(request.desiredStopTime ?? light.civilDusk ?? latestByWalking, latestByWalking);
    const budget = Math.max(0, (stopTime - now) / 1000);

    // Weather: an expired forecast is never used for planning.
    const freshness = forecast ? forecastFreshness(forecast, now) : undefined;
    const usableForecast = freshness !== undefined && compareFreshness(freshness, 'expired') < 0 ? forecast : undefined;
    const usesSynthetic =
      this.data.containsSyntheticData || forecast?.isSynthetic === true || forecast?.provenance.isSynthetic === true;

    // Position on the route.
    const projection = isValidCoordinate(request.position) ? this.track.project(request.position) : undefined;
    const distanceAlong = projection?.distanceAlong ?? 0;
    const crossTrack = projection?.crossTrackDistance ?? 0;

    let options: OvernightOption[] = [];
    if (projection) {
      const context: PlanningContext = {
        request,
        origin: this.origin(request, distanceAlong, crossTrack),
        sunset: light.sunset,
        civilDusk: light.civilDusk,
        forecast: usableForecast,
        searchRange: this.searchRange(request, distanceAlong, maxWalking),
      };
      options = [...this.poiOptions(context, availability), ...this.zoneOptions(context, availability)];
    } else {
      BivouacLog.logger.notice('Sleep Tonight: position could not be projected on the route');
    }
    const groups = SleepTonightPlanner.grouped(options, budget, request.preference);
    // An unsuitable place (e.g. a shelter inside a prohibited area) does not count as reachable.
    const anyWithinBudget = options.some((o) => o.estimatedDuration <= budget && o.status !== 'unsuitable');

    // Warnings.
    const warnings: PlannerWarning[] = [];
    if (crossTrack > SleepTonightPlanner.offRouteThresholdMeters) {
      warnings.push({ kind: 'offRoute', distance: crossTrack });
    }
    if (crossTrack > SleepTonightPlanner.routeCorridorMeters) warnings.push({ kind: 'outsideRouteCorridor' });
    if (light.sunset !== undefined) {
      const minutes = (light.sunset - now) / 60_000;
      if (minutes <= 0) warnings.push({ kind: 'afterSunset' });
      else if (minutes < SleepTonightPlanner.lateInDayMinutes) {
        warnings.push({ kind: 'lateInDay', minutesToSunset: Math.floor(minutes) });
      }
    }
    if (usableForecast) {
      let referenceElevation: number | undefined;
      for (const group of groups) {
        for (const option of group.options) {
          const elevation = option.poi?.elevation ?? option.candidate?.elevation;
          if (elevation === undefined || !Number.isFinite(elevation)) continue;
          if (referenceElevation === undefined || elevation > referenceElevation) referenceElevation = elevation;
        }
      }
      warnings.push(
        ...SleepTonightPlanner.weatherWarnings(
          usableForecast,
          now,
          stopTime,
          referenceElevation ?? request.elevation,
          configuration,
        ),
      );
    }
    if (forecast && freshness !== undefined && compareFreshness(freshness, 'stale') >= 0) {
      warnings.push({ kind: 'staleWeather', fetchedAt: forecast.fetchedAt });
    }
    // An expired forecast is not used, so the plan has no usable weather: say so explicitly instead of showing an empty
    // (reassuring-looking) list of weather warnings.
    if (!usableForecast) warnings.push({ kind: 'noWeather' });
    if (usesSynthetic) warnings.push({ kind: 'syntheticData' });
    if (!anyWithinBudget) warnings.push({ kind: 'noOptionsWithinReach' });

    BivouacLog.logger.debug(
      `Sleep Tonight: ${options.length} options, ${groups.length} groups, ${warnings.length} warnings`,
    );
    const result: SleepTonightResult = {
      generatedAt: now,
      request,
      distanceAlong,
      crossTrackDistance: crossTrack,
      sunset: light.sunset,
      civilDusk: light.civilDusk,
      availableWalkingTime: budget,
      groups,
      warnings,
      usesSyntheticData: usesSynthetic,
    };
    if (forecast) {
      result.weatherFetchedAt = forecast.fetchedAt;
      result.weatherFreshness = freshness;
      result.weatherProvenance = forecast.provenance;
    }
    return result;
  }

  // MARK: Daylight

  /**
   * Daylight for the night the hiker is in or approaching. The solar calculator works on the UTC day, which starts
   * around 01:00–02:00 local time in the Alps; before that day's civil dawn it is still night, so the previous
   * evening's sunset and dusk apply (after sunset, no walking budget) instead of the coming evening's.
   */
  static daylight(now: EpochMs, position: GeoCoordinate): DayLight {
    const today = solarDaylight(now, position);
    if (today.civilDawn === undefined || !(now < today.civilDawn)) return today;
    return solarDaylight(now - 86_400_000, position);
  }

  // MARK: Options

  poiOptions(context: PlanningContext, availability: (coordinate: GeoCoordinate) => Availability): OvernightOption[] {
    const request = context.request;
    const range = context.searchRange;
    const pois = this.data
      .pointsOfInterestIn(range.bounds, OvernightFacilities.categories)
      .slice()
      .sort((a, b) => compareStrings(a.id, b.id));
    const water = this.waterLookup(range);
    const result: OvernightOption[] = [];
    for (const poi of pois) {
      const category = overnightCategoryOf(poi.category);
      if (!category || !isValidCoordinate(poi.coordinate)) continue;
      const leg = this.leg(poi.coordinate, poi.elevation, context.origin, range.projectionWindow);
      if (!leg || !(leg.projection.crossTrackDistance <= range.straightLineDetourLimit)) continue;
      if (!this.isInRange(leg.delta, context)) continue;

      const sourceConfidence: Confidence = poi.provenance.isSynthetic ? 'low' : poi.confidence;
      const snapshot = WeatherAdjustment.snapshot(context.forecast, leg.arrival, poi.elevation);
      const weather = this.zoneGenerator.evaluator.weather(snapshot, 'unknown');
      const reasons: BivouacReason[] = [];
      let regulatoryState: RegulatoryState;
      let regulatoryConfidence: Confidence;
      let status: SuitabilityStatus;
      if (OvernightFacilities.isOfficial(poi)) {
        regulatoryState = 'official';
        regulatoryConfidence = sourceConfidence;
        status = SleepTonightPlanner.officialStatus(poi, leg.arrival, weather?.score);
      } else {
        const assessment = this.resolver.assess(poi.coordinate, request.now, this.data, request.userReports);
        regulatoryState = assessment.state;
        regulatoryConfidence = assessment.confidence;
        status = SleepTonightPlanner.unofficialShelterStatus(assessment.state);
        reasons.push(...assessment.reasons);
      }
      reasons.push(...(weather?.reasons ?? []));
      if (poi.provenance.isSynthetic) {
        reasons.push({ kind: { kind: 'syntheticData' }, impact: 'caution', category: 'dataQuality' });
      }

      result.push(
        this.makeOption({
          id: `poi:${poi.id}`,
          category,
          name: poi.name === '' ? undefined : poi.name,
          poi,
          coordinate: poi.coordinate,
          leg,
          context,
          forecastAtArrival: snapshot,
          nearestWater: water.nearest(poi.coordinate, SleepTonightPlanner.waterSearchRadiusMeters)?.distance,
          regulatoryState,
          regulatoryConfidence,
          sourceConfidence,
          availability: availability(poi.coordinate),
          status,
          reasons,
        }),
      );
    }
    return result;
  }

  zoneOptions(context: PlanningContext, availability: (coordinate: GeoCoordinate) => Availability): OvernightOption[] {
    const range = context.searchRange;
    const origin = context.origin;
    const window = range.projectionWindow;
    const zones = this.zoneGenerator.evaluatedZones(this.track, range.start, range.end, this.data, {
      date: context.request.now,
      maxDetourMeters: range.straightLineDetourLimit,
      forecast: context.forecast,
      arrivalEstimate: (coordinate) => this.leg(coordinate, undefined, origin, window)?.arrival ?? origin.now,
      userReports: context.request.userReports,
      minimumZoneRadiusMeters: 200,
      // Over-fetch: the time budget and the per-category limit are applied afterwards.
      maximumZoneCount: SleepTonightPlanner.zoneFetchLimit,
    });
    const water = this.waterLookup(range);
    const result: OvernightOption[] = [];
    for (const zone of zones) {
      const candidate = zone.candidate;
      const leg = this.leg(candidate.center, candidate.elevation, origin, window);
      if (!leg || !this.isInRange(leg.delta, context)) continue;
      let regulatoryState = candidate.regulatoryState;
      if (regulatoryState === 'official') {
        // Search areas are never official (defensive; the resolver cannot produce it).
        BivouacLog.logger.fault('Bivouac zone carried .official; downgraded to unknown');
        regulatoryState = 'unknown';
      }
      result.push(
        this.makeOption({
          id: candidate.id,
          category: 'bivouacSearchArea',
          candidate,
          coordinate: candidate.center,
          area: candidate.area,
          leg,
          context,
          forecastAtArrival: WeatherAdjustment.snapshot(context.forecast, leg.arrival, candidate.elevation),
          nearestWater: water.nearest(candidate.center, SleepTonightPlanner.waterSearchRadiusMeters)?.distance,
          regulatoryState,
          regulatoryConfidence: zone.regulatoryConfidence,
          sourceConfidence: zone.terrainConfidence,
          availability: availability(candidate.center),
          status: regulatoryState === 'unknown' ? 'insufficientData' : candidate.status,
          reasons: candidate.reasons,
        }),
      );
    }
    return result;
  }

  makeOption(input: OptionInput): OvernightOption {
    const { leg, context } = input;
    const afterSunset = context.sunset !== undefined ? leg.arrival > context.sunset : false;
    const desiredStop = context.request.desiredStopTime;
    const afterDesiredStop = desiredStop !== undefined ? leg.arrival > desiredStop : false;
    const allReasons = input.reasons.slice();
    if (afterSunset) allReasons.push({ kind: { kind: 'arrivalAfterSunset' }, impact: 'caution', category: 'access' });
    const unique: BivouacReason[] = [];
    for (const r of allReasons) if (!containsReason(unique, r)) unique.push(r);
    const requested = context.request.maxAdditionalWalking;
    const reference = Number.isFinite(requested) && requested > 0 ? requested : 3 * 3600;
    const option: OvernightOption = {
      id: input.id,
      category: input.category,
      coordinate: input.coordinate,
      distanceAlongRoute: leg.delta,
      detourMeters: leg.detour,
      totalDistance: leg.total,
      ascent: leg.ascent,
      descent: leg.descent,
      estimatedDuration: leg.duration,
      estimatedArrival: leg.arrival,
      isBehind: leg.delta < 0,
      arrivesAfterSunset: afterSunset,
      arrivesAfterDesiredStop: afterDesiredStop,
      regulatoryState: input.regulatoryState,
      regulatoryConfidence: input.regulatoryConfidence,
      sourceConfidence: input.sourceConfidence,
      availability: input.availability,
      status: input.status,
      reasons: unique,
      rankScore: SleepTonightPlanner.rankScore(
        input.status,
        leg.arrival,
        leg.duration,
        context.civilDusk,
        minConfidence(input.regulatoryConfidence, input.sourceConfidence),
        reference,
      ),
    };
    if (input.name !== undefined) option.name = input.name;
    if (input.poi) option.poi = input.poi;
    if (input.candidate) option.candidate = input.candidate;
    if (input.area) option.area = input.area;
    if (input.forecastAtArrival) option.forecastAtArrival = input.forecastAtArrival;
    if (input.nearestWater !== undefined) option.nearestWaterDistance = input.nearestWater;
    return option;
  }

  // MARK: Legs

  origin(request: SleepTonightRequest, along: number, crossTrack: number): Origin {
    const origin: Origin = { along, now: request.now, returnDistance: 0, returnAscent: 0, returnDescent: 0 };
    if (!(crossTrack > SleepTonightPlanner.onRouteToleranceMeters)) return origin;
    origin.returnDistance = crossTrack * SleepTonightPlanner.detourFactor;
    const hiker = request.elevation;
    const route = this.track.elevationAtDistance(along);
    if (hiker !== undefined && Number.isFinite(hiker) && route !== undefined) {
      origin.returnAscent = Math.max(0, route - hiker);
      origin.returnDescent = Math.max(0, hiker - route);
    }
    return origin;
  }

  searchRange(request: SleepTonightRequest, along: number, maxWalking: number): SearchRange {
    const track = this.track;
    // Upper bound of the distance reachable in the budget: flat walking at the DIN horizontal speed.
    const speed = (HikingTime.horizontalSpeedKmh * 1000) / 3600 / this.estimator.personalFactor;
    const reach = speed * maxWalking;
    const extended = Math.max(2000, Math.min(2 * reach, reach + 10_000));
    const end = Math.min(track.totalDistance, along + extended);
    const behind = Number.isFinite(request.behindLimitMeters) ? Math.max(0, request.behindLimitMeters) : 0;
    const start = request.includeOptionsBehind ? Math.max(0, along - behind) : along;
    const detourLimit = Number.isFinite(request.maxDetourMeters)
      ? Math.max(0, request.maxDetourMeters) / SleepTonightPlanner.detourFactor
      : 0;
    const fallback = track.coordinateAtDistance(along) ?? request.position;
    const slice = sliceTrack(track, start, end);
    const stretchBounds = (slice ? boundsOf(slice.coordinates) : undefined) ?? {
      minLatitude: fallback.latitude,
      minLongitude: fallback.longitude,
      maxLatitude: fallback.latitude,
      maxLongitude: fallback.longitude,
    };
    return {
      start,
      end,
      bounds: boundsExpanded(stretchBounds, detourLimit),
      projectionWindow: Math.max(end - along, along - start) + detourLimit + 500,
      straightLineDetourLimit: detourLimit,
    };
  }

  leg(target: GeoCoordinate, elevation: number | undefined, origin: Origin, window: number): Leg | undefined {
    const track = this.track;
    const projection = track.project(target, origin.along, window);
    if (!projection) return undefined;
    const delta = projection.distanceAlong - origin.along;
    const alongRoute = track.ascentDescent(origin.along, projection.distanceAlong);
    let ascent = alongRoute.ascent + origin.returnAscent;
    let descent = alongRoute.descent + origin.returnDescent;
    const routeElevation = track.elevationAtDistance(projection.distanceAlong);
    if (elevation !== undefined && Number.isFinite(elevation) && routeElevation !== undefined) {
      ascent += Math.max(0, elevation - routeElevation);
      descent += Math.max(0, routeElevation - elevation);
    }
    const detour = projection.crossTrackDistance * SleepTonightPlanner.detourFactor;
    const total = origin.returnDistance + Math.abs(delta) + detour;
    const duration = this.estimator.duration(total, ascent, descent);
    return { projection, delta, detour, total, ascent, descent, duration, arrival: origin.now + duration * 1000 };
  }

  isInRange(delta: number, context: PlanningContext): boolean {
    const request = context.request;
    const range = context.searchRange;
    if (delta > range.end - context.origin.along + 1) return false;
    if (!(delta < 0)) return true;
    if (!request.includeOptionsBehind) return delta >= -SleepTonightPlanner.behindToleranceMeters;
    return -delta <= context.origin.along - range.start + SleepTonightPlanner.behindToleranceMeters;
  }

  waterLookup(range: SearchRange): POIProximity {
    const area = boundsExpanded(range.bounds, SleepTonightPlanner.waterSearchRadiusMeters);
    return new POIProximity(this.data.pointsOfInterestIn(area, waterCategory));
  }

  // MARK: Status, ranking, grouping

  /**
   * Designated facilities are `suitable`, or `caution` when out of their operating season on arrival or (campsites
   * only) when the weather score at arrival is below 0.5.
   */
  static officialStatus(poi: PointOfInterest, arrival: EpochMs, weatherScore: number | undefined): SuitabilityStatus {
    const season = poi.attributes.season;
    if (season && poi.attributes.isYearRound !== true && !seasonContains(season, arrival)) return 'caution';
    if (
      poi.category === 'camping' &&
      weatherScore !== undefined &&
      !(weatherScore >= SuitabilityClassifier.weatherCautionThreshold)
    ) {
      return 'caution';
    }
    return 'suitable';
  }

  /** Non-designated shelters are never green: blocking → unsuitable, unknown → insufficient data, else caution. */
  static unofficialShelterStatus(state: RegulatoryState): SuitabilityStatus {
    switch (state) {
      case 'restricted':
      case 'prohibited':
        return 'unsuitable';
      case 'unknown':
        return 'insufficientData';
      case 'official':
      case 'noKnownRestriction':
      case 'caution':
        return 'caution';
    }
  }

  /**
   * Lexicographic ranking packed in one number: status (3/2/1/0) + arrival before dusk (0.5) + effort (0…0.4, less
   * walking is better) + confidence (0…0.05). `duration` and `reference` are seconds.
   */
  static rankScore(
    status: SuitabilityStatus,
    arrival: EpochMs,
    duration: number,
    civilDusk: EpochMs | undefined,
    confidence: Confidence,
    reference: number,
  ): number {
    const statusRank = status === 'suitable' ? 3 : status === 'caution' ? 2 : status === 'insufficientData' ? 1 : 0;
    const beforeDusk = civilDusk !== undefined ? arrival <= civilDusk : true;
    const effort = 1 - Math.min(1, Math.max(0, duration) / Math.max(reference, 1800));
    return statusRank + (beforeDusk ? 0.5 : 0) + 0.4 * effort + (0.05 * confidenceRankValue(confidence)) / 2;
  }

  /**
   * Groups options by category in preference order. Within the budget the best `maximumOptionsPerCategory` are kept; a
   * category with nothing within budget keeps its single nearest (shortest walk) option so the hiker always sees an
   * alternative.
   */
  static grouped(
    options: readonly OvernightOption[],
    budget: number,
    preference: OvernightPreference,
  ): OvernightGroup[] {
    const preferred = preferredCategories(preference);
    const order = [...preferred, ...allOvernightCategories.filter((c) => !preferred.includes(c))];
    const groups: OvernightGroup[] = [];
    for (const category of order) {
      const all = options.filter((o) => o.category === category);
      const within = all.filter((o) => o.estimatedDuration <= budget).sort(SleepTonightPlanner.compareRanked);
      let kept = within.slice(0, SleepTonightPlanner.maximumOptionsPerCategory);
      if (kept.length === 0) {
        let nearest: OvernightOption | undefined;
        for (const o of all) {
          if (
            !nearest ||
            o.estimatedDuration < nearest.estimatedDuration ||
            (o.estimatedDuration === nearest.estimatedDuration && o.id < nearest.id)
          ) {
            nearest = o;
          }
        }
        if (nearest) kept = [nearest];
      }
      if (kept.length > 0) groups.push({ category, options: kept });
    }
    return groups;
  }

  static isRankedBefore(a: OvernightOption, b: OvernightOption): boolean {
    if (a.rankScore !== b.rankScore) return a.rankScore > b.rankScore;
    if (a.estimatedDuration !== b.estimatedDuration) return a.estimatedDuration < b.estimatedDuration;
    return a.id < b.id;
  }

  /** `isRankedBefore` as an `Array.sort` comparator. */
  static compareRanked(a: OvernightOption, b: OvernightOption): number {
    if (SleepTonightPlanner.isRankedBefore(a, b)) return -1;
    if (SleepTonightPlanner.isRankedBefore(b, a)) return 1;
    return 0;
  }

  // MARK: Weather warnings

  /**
   * Thunderstorm (≥ moderate) and strong wind (gusts ≥ `strongWindGustKmh`, or without gusts a mean wind ≥
   * `strongMeanWindKmh`) between now and the end of the night; night temperature below the cold threshold, adjusted to
   * `referenceElevation`; `noWeather` when the forecast does not cover the window.
   */
  static weatherWarnings(
    forecast: WeatherForecast,
    now: EpochMs,
    stopTime: EpochMs,
    referenceElevation: number | undefined,
    configuration: Readonly<BivouacScoringConfiguration>,
  ): PlannerWarning[] {
    const nightStart = Math.max(stopTime, now);
    const end = nightStart + SleepTonightPlanner.nightDuration * 1000;
    const windowStart = now - 1800 * 1000;
    const window = forecast.hourly.filter((s) => s.time >= windowStart && s.time <= end);
    if (window.length === 0) return [{ kind: 'noWeather' }];
    const warnings: PlannerWarning[] = [];

    let risk: ThunderstormRisk | undefined;
    for (const snapshot of window) {
      let r = snapshot.thunderstormRisk;
      if (snapshot.condition === 'thunderstorm' && compareThunderstormRisk(r, 'moderate') < 0) r = 'moderate';
      if (risk === undefined || compareThunderstormRisk(r, risk) > 0) risk = r;
    }
    if (risk === 'moderate' || risk === 'high') warnings.push({ kind: 'thunderstormRisk', risk });

    // Gusts when forecast, otherwise the mean wind — each against its own threshold (a mean wind of 40 km/h stands for
    // gusts around 60 km/h in the mountains) and reported as such, never as gusts.
    let strongest: { value: number; isGust: boolean; severity: number } | undefined;
    for (const snapshot of window) {
      let entry: { value: number; isGust: boolean; severity: number } | undefined;
      const { windGust: gust, windSpeed: mean } = snapshot;
      if (gust !== undefined && Number.isFinite(gust)) {
        entry = { value: gust, isGust: true, severity: gust / configuration.strongWindGustKmh };
      } else if (mean !== undefined && Number.isFinite(mean)) {
        entry = { value: mean, isGust: false, severity: mean / configuration.strongMeanWindKmh };
      }
      if (entry && (!strongest || entry.severity > strongest.severity)) strongest = entry;
    }
    if (strongest && strongest.severity >= 1) {
      warnings.push(
        strongest.isGust
          ? { kind: 'strongWind', gustKmh: strongest.value }
          : { kind: 'strongMeanWind', kmh: strongest.value },
      );
    }

    let coldest: number | undefined;
    for (const snapshot of window) {
      if (!(snapshot.time >= nightStart) || !Number.isFinite(snapshot.temperature)) continue;
      const t = WeatherAdjustment.temperature(snapshot.temperature, forecast.elevation, referenceElevation);
      if (coldest === undefined || t < coldest) coldest = t;
    }
    if (coldest !== undefined && coldest < configuration.coldNightCelsius) {
      warnings.push({ kind: 'coldNight', celsius: coldest });
    }
    return warnings;
  }
}
